<template>
  <div :class="b()">
    <c-breadcrumb v-if="$viewport.isSm" />
    <div :class="b('wrapper')">
      <h1 :class="b('name')">
        {{ location.displayName }}
      </h1>
      <aside :class="b('controls')">
        <div :class="b('links-wrapper')">
          <div v-if="locationDistance" :class="b('location-distance')">
            <e-icon icon="i-distance" size="19" />
            {{ $t('l-location-detail.locationDistanceLabel') }} : {{ locationDistance }}
          </div>
          <button v-else
                  :aria-label="$t('l-location-detail.calcDistanceButtonLabel')"
                  :class="b('calculate-distance-button')"
                  type="button"
                  @click="requestCurrentPositionFromGeolocation"
          >
            <e-loader v-if="isRequestingLocation" variant="tiny" />
            <e-icon v-else icon="i-distance" size="19" />
            {{ $t('l-location-detail.calcDistanceButtonLabel') }}
          </button>
          <a :href="googleDirectionUrl"
             :class="b('link')"
             rel="noopener noreferrer"
             target="_blank"
          >
            <e-icon icon="i-route" size="19" />
            {{ $t('l-location-detail.locationDirectionsLinkLabel') }}
          </a>
          <ul :class="b('list')">
            <li v-for="link in location.links"
                :key="link.uid"
            >
              <a v-bind="cmsLinkAttributes(link)"
                 :class="b('link')"
              >
                <e-icon v-if="isVirtualTour(link)" icon="i-virtual-tour" size="19" />
                {{ link.name }}
              </a>
            </li>
          </ul>
        </div>
        <button
          v-if="sessionStore.flags.storeSelectionEnabled"
          :class="b('toggle-favorite-button')"
          type="button"
          @click="handleUpdateDefaultSelection(location)"
        >
          {{ toggleFavoriteLabel }}
          <e-icon :icon="location.favorite ? 'i-close' : 'i-pin'" :size="location.favorite ? '18' : '20'" />
        </button>
      </aside>
      <aside :class="b('map-container')">
        <c-google-maps
          v-if="googleMapsLocation"
          ref="map"
          :locations="[googleMapsLocation]"
          :class="b('map')"
          :maps-config="{ draggableCursor: 'default' }"
          :has-click-listener="false"
          :clickable-markers="false"
        />
      </aside>
      <c-location-tile-detailed :location="location" />
    </div>
    <div v-if="location.cmsComponents?.length" :class="b('cms-components')">
      <c-cms-components-wrapper :components="location.cmsComponents" />
    </div>

  </div>
</template>

<script lang="ts">
  import { defineComponent } from 'vue';
  import { Layout } from '@/setup/globals';
  import cBreadcrumb from '@/components/c-breadcrumb.vue';
  import cGoogleMaps, { GoogleMapsLocation } from '@/components/c-google-maps.vue';
  import eIcon from '@/elements/e-icon.vue';
  import eLoader from '@/elements/e-loader.vue';
  import cLocationTileDetailed from '@/components/c-location-tile-detailed.vue';
  import cCmsComponentsWrapper from '@/components/c-cms-components-wrapper.vue';
  import useLocationDetailStore from '@/stores/location-detail';
  import useNotificationStore from '@/stores/notification';
  import useSessionStore from '@/stores/session';
  import getGoogleDirectionUrl from '@/helpers/google-direction-url';
  import getMarkerIcon from '@/helpers/google-marker-icon';
  import setActiveLayout from '@/compositions/set-active-layout';
  import cmsLinkAttributes from '@/helpers/cms-link-attributes';
  import { PointOfService } from '@/types/point-of-service';
  import { CmsLink } from '@/types/cms-link';

  const layout = Layout.LocationDetail;

  type Setup = {
    locationDetailStore: ReturnType<typeof useLocationDetailStore>;
    sessionStore: ReturnType<typeof useSessionStore>;
    cmsLinkAttributes: typeof cmsLinkAttributes;
  }

  type Data = {
    isRequestingLocation: boolean;
    googleMapsLocation?: GoogleMapsLocation;
    userGeoPosition?: google.maps.LatLng;
    locationGeoPosition?: google.maps.LatLng;
    locationDistance?: string;
  }

  /**
   * Renders the location detail page with a Google Map and the location details.
   */
  export default defineComponent({
    name: layout,

    components: {
      cCmsComponentsWrapper,
      cLocationTileDetailed,
      cGoogleMaps,
      eIcon,
      eLoader,
      cBreadcrumb,
    },

    // props: {},
    // emits: {},

    setup(): Setup {
      setActiveLayout(layout);

      return {
        sessionStore: useSessionStore(),
        locationDetailStore: useLocationDetailStore(),
        cmsLinkAttributes,
      };
    },
    data(): Data {
      return {
        isRequestingLocation: false,
        googleMapsLocation: undefined,
        userGeoPosition: undefined,
        locationGeoPosition: undefined,
        locationDistance: undefined,
      };
    },

    computed: {
      location(): PointOfService {
        return this.locationDetailStore.location;
      },

      toggleFavoriteLabel(): string {
        return this.$t(this.location.favorite
          ? 'l-location-detail.removeAsFavoriteLocation'
          : 'l-location-detail.setAsFavoriteLocation');
      },

      googleDirectionUrl(): string {
        return getGoogleDirectionUrl(this.userGeoPosition, this.location?.geoPoint, 'driving');
      },
    },
    watch: {
      location: {
        handler() {
          this.googleMapsLocation = this.getMappedLocation();
        },
        immediate: true,
      },
    },
    // beforeCreate() {},
    // created() {},
    // beforeMount() {},
    // mounted() {},
    // beforeUpdate() {},
    // updated() {},
    // activated() {},
    // deactivated() {},
    // beforeUnmount() {},
    // unmounted() {},

    methods: {
      getMappedLocation(): GoogleMapsLocation {
        const { location } = this;

        const mappedLocation: GoogleMapsLocation = {
          lat: location.geoPoint?.latitude,
          lng: location.geoPoint?.longitude,
          geocode: location.name,
          title: location.displayName,
          icon: getMarkerIcon(location.favorite),
        };

        return mappedLocation;
      },

      isVirtualTour(link: CmsLink): boolean | undefined {
        if (!link.linkTarget) {
          return undefined;
        }

        return link.uid.includes('google360');
      },

      handleUpdateDefaultSelection(location: PointOfService): void {
        const favorite = !location.favorite;

        this.sessionStore.apiUpdateFavoriteStore(location, favorite)
          .then(() => {
            this.locationDetailStore.location = {
              ...location,
              favorite: favorite && this.locationDetailStore.location.name === location.name,
            };
          });
      },

      requestCurrentPositionFromGeolocation(): void {
        this.isRequestingLocation = true;

        navigator.geolocation.getCurrentPosition(
          this.calculateLocationDistance,
          this.showGeolocationError,
          {
            enableHighAccuracy: true,
            timeout: 3000,
            maximumAge: 0,
          }
        );
      },

      calculateLocationDistance(position: GeolocationPosition): void {
        this.isRequestingLocation = false;

        this.userGeoPosition = new google.maps.LatLng({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        });

        if (!this.location.geoPoint?.latitude && !this.location.geoPoint?.longitude) {
          return;
        }

        this.locationGeoPosition = new google.maps.LatLng({
          lat: this.location.geoPoint?.latitude,
          lng: this.location.geoPoint?.longitude,
        });

        if (this.locationGeoPosition && this.userGeoPosition) {
          const distanceService = new google.maps.DistanceMatrixService();

          distanceService.getDistanceMatrix({
            origins: [this.userGeoPosition],
            destinations: [this.locationGeoPosition],
            travelMode: google.maps.TravelMode.DRIVING,
          }, (response, status) => {
            if (status === 'OK') {
              this.locationDistance = response?.rows[0]?.elements[0]?.distance?.text;
            }
          });
        }
      },

      showGeolocationError(error: GeolocationPositionError): void {
        console.error(error.message); // eslint-disable-line no-console

        this.isRequestingLocation = false;

        useNotificationStore().showNotification({
          message: this.$t('l-location-finder.browserLocationError'),
          type: 'error',
          expire: true,
        });
      },
    },
    // render() {},
  });
</script>

<style lang="scss">
  @use '@/setup/scss/mixins';
  @use '@/setup/scss/variables';

  .l-location-detail {
    &__wrapper {
      display: grid;
      grid-template-areas:
        'map'
        'name'
        'controls'
        'location';
      grid-template-columns: 1fr;
      gap: variables.$spacing--40 variables.$spacing--20;

      @include mixins.media(md) {
        grid-template-areas:
          'name name'
          'controls controls'
          'location map';
        grid-template-columns: 1fr 2fr;
      }

      @include mixins.media(lg) {
        grid-template-areas:
          'name controls'
          'location map';
        grid-template-columns: 1fr 2fr;
      }
    }

    &__name {
      @include mixins.heading-h2(false);

      grid-area: name;
      margin: 0;

      @include mixins.media($down: xs) {
        padding-bottom: variables.$spacing--5;
        border-bottom: 4px solid variables.$color-grayscale--0;
      }
    }

    &__controls {
      display: flex;
      grid-area: controls;
      gap: variables.$spacing--10;
      flex-wrap: wrap;
      justify-content: space-between;
      align-items: center;
    }

    &__links-wrapper {
      display: flex;
      gap: variables.$spacing--10;
      flex-wrap: wrap;
      align-items: center;

      @include mixins.media(sm) {
        gap: variables.$spacing--20;
      }
    }

    &__toggle-favorite-button,
    &__calculate-distance-button {
      display: flex;
      gap: variables.$spacing--10;
      align-items: center;
      cursor: pointer;

      &:hover,
      &:focus {
        color: variables.$color-primary--1;
      }
    }

    &__location-distance {
      display: flex;
      gap: variables.$spacing--10;
      align-items: center;
    }

    &__list {
      display: inline-flex;
      gap: variables.$spacing--40;
      list-style: none;
    }

    &__link {
      display: flex;
      gap: variables.$spacing--10;
      align-items: center;

      &:hover,
      &:focus {
        color: variables.$color-primary--1;
      }
    }

    &__map-container {
      grid-area: map;

      @include mixins.media(md) {
        position: sticky;
        top: calc(var(--header-height, 0) + #{variables.$spacing--20});
        height: 50vh;
      }
    }

    &__cms-components {
      margin-top: variables.$spacing--50;
    }

    .c-location-tile-detailed {
      grid-area: location;
    }
  }
</style>
